<script setup>
import Colors from '@/config/Colors.json';
import { watch, ref, onMounted, toRaw, computed } from 'vue';
import { getClient } from '@/core/js/GraphqlClient.js';
import Loader from '@/components/loader/Loader.vue';
import { GA } from '@/mixins/Ga';
import { useStore } from '@/store/Store.js';
import { useLenis } from '@/components/scroll/Lenis.js';
import GeoPin from '@/icons/geo-pin.svg';
import ListingIcon from '@/icons/listing.svg';
import GeoMap from '@/icons/geo-map.svg';
import MagnifyingGlass from '@/icons/magnifying-glass.svg';

const lenis = useLenis();

defineProps({
    data: { type: Object, default: () => {} },
    color: { type: String, default: '' },
    mainColor: {
        type: String,
        default: '',
    },
});

const store = useStore();
const limit = ref(10);
const offset = ref(0);
const error = ref('');
const stores = ref([]);
const postcode = ref('');
const variables = ref({ lang: store.lang.value });
const client = getClient();
const userLoc = ref({ lat: null, long: null });
const gmap = ref(null);
const markerClusterer = ref(null);
const defaultLoc = ref({ lat: 46.8, lng: -71.2 });
const mapOpened = ref(true);
const center = { lat: 45.880105636313914, lng: -72.48861089371046 };
const openedMarkerID = ref(null);
const markers = ref([]);
const unclusteredMarkers = ref([]);
const firstRun = ref(true);
const globalIndex = ref(0);
const defaultViewZoom = ref(13);
const defaultMaxZoom = ref(15);
const displayedStoresCount = ref(6);
const loadMoreIncrement = 6;
const isLoadingMore = ref(false);
const searchTerm = ref('');
const debouncedSearchTerm = ref('');
const isSearchingNormal = ref(false);
const isSearchingPostCode = ref(false);
const isSearching = computed(() => isSearchingNormal.value || isSearchingPostCode.value);
// const mapStyles = [
//     {
//         featureType: 'water',
//         elementType: 'geometry',
//         stylers: [
//             {
//                 color: '#e9e9e9',
//             },
//             {
//                 lightness: 17,
//             },
//         ],
//     },
//     {
//         featureType: 'landscape',
//         elementType: 'geometry',
//         stylers: [
//             {
//                 color: '#f5f5f5',
//             },
//             {
//                 lightness: 20,
//             },
//         ],
//     },
//     {
//         featureType: 'road.highway',
//         elementType: 'geometry.fill',
//         stylers: [
//             {
//                 color: '#ffffff',
//             },
//             {
//                 lightness: 17,
//             },
//         ],
//     },
//     {
//         featureType: 'road.highway',
//         elementType: 'geometry.stroke',
//         stylers: [
//             {
//                 color: '#ffffff',
//             },
//             {
//                 lightness: 29,
//             },
//             {
//                 weight: 0.2,
//             },
//         ],
//     },
//     {
//         featureType: 'road.arterial',
//         elementType: 'geometry',
//         stylers: [
//             {
//                 color: '#ffffff',
//             },
//             {
//                 lightness: 18,
//             },
//         ],
//     },
//     {
//         featureType: 'road.local',
//         elementType: 'geometry',
//         stylers: [
//             {
//                 color: '#ffffff',
//             },
//             {
//                 lightness: 16,
//             },
//         ],
//     },
//     {
//         featureType: 'poi',
//         elementType: 'geometry',
//         stylers: [
//             {
//                 color: '#f5f5f5',
//             },
//             {
//                 lightness: 21,
//             },
//         ],
//     },
//     {
//         featureType: 'poi.park',
//         elementType: 'geometry',
//         stylers: [
//             {
//                 color: '#dedede',
//             },
//             {
//                 lightness: 21,
//             },
//         ],
//     },
//     {
//         elementType: 'labels.text.stroke',
//         stylers: [
//             {
//                 visibility: 'on',
//             },
//             {
//                 color: '#ffffff',
//             },
//             {
//                 lightness: 16,
//             },
//         ],
//     },
//     {
//         elementType: 'labels.text.fill',
//         stylers: [
//             {
//                 saturation: 36,
//             },
//             {
//                 color: '#333333',
//             },
//             {
//                 lightness: 40,
//             },
//         ],
//     },
//     {
//         elementType: 'labels.icon',
//         stylers: [
//             {
//                 visibility: 'off',
//             },
//         ],
//     },
//     {
//         featureType: 'transit',
//         elementType: 'geometry',
//         stylers: [
//             {
//                 color: '#f2f2f2',
//             },
//             {
//                 lightness: 19,
//             },
//         ],
//     },
//     {
//         featureType: 'administrative',
//         elementType: 'geometry.fill',
//         stylers: [
//             {
//                 color: '#fefefe',
//             },
//             {
//                 lightness: 20,
//             },
//         ],
//     },
//     {
//         featureType: 'administrative',
//         elementType: 'geometry.stroke',
//         stylers: [
//             {
//                 color: '#fefefe',
//             },
//             {
//                 lightness: 17,
//             },
//             {
//                 weight: 1.2,
//             },
//         ],
//     },
// ];
const mapStyles = [
    {
        featureType: 'administrative.neighborhood',
        elementType: 'geometry.fill',
        stylers: [
            {
                visibility: 'on',
            },
            {
                hue: '#ff0000',
            },
        ],
    },
    {
        featureType: 'administrative.neighborhood',
        elementType: 'labels.text.fill',
        stylers: [
            {
                visibility: 'off',
            },
        ],
    },
    {
        featureType: 'administrative.neighborhood',
        elementType: 'labels.text.stroke',
        stylers: [
            {
                visibility: 'off',
            },
        ],
    },
    {
        featureType: 'landscape.man_made',
        elementType: 'geometry',
        stylers: [
            {
                color: '#f7f1df',
            },
        ],
    },
    {
        featureType: 'landscape.natural',
        elementType: 'geometry',
        stylers: [
            {
                color: '#d0e3b4',
            },
        ],
    },
    {
        featureType: 'landscape.natural.terrain',
        elementType: 'geometry',
        stylers: [
            {
                visibility: 'off',
            },
        ],
    },
    {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [
            {
                visibility: 'off',
            },
        ],
    },
    {
        featureType: 'poi.business',
        elementType: 'all',
        stylers: [
            {
                visibility: 'off',
            },
        ],
    },
    {
        featureType: 'poi.medical',
        elementType: 'geometry',
        stylers: [
            {
                color: '#fbd3da',
            },
        ],
    },
    {
        featureType: 'poi.park',
        elementType: 'geometry',
        stylers: [
            {
                color: '#bde6ab',
            },
        ],
    },
    {
        featureType: 'road',
        elementType: 'geometry.stroke',
        stylers: [
            {
                visibility: 'off',
            },
        ],
    },
    {
        featureType: 'road.highway',
        elementType: 'geometry.fill',
        stylers: [
            {
                color: '#ffe15f',
            },
        ],
    },
    {
        featureType: 'road.highway',
        elementType: 'geometry.stroke',
        stylers: [
            {
                color: '#efd151',
            },
        ],
    },
    {
        featureType: 'road.arterial',
        elementType: 'geometry.fill',
        stylers: [
            {
                color: '#ffffff',
            },
        ],
    },
    {
        featureType: 'road.local',
        elementType: 'geometry.fill',
        stylers: [
            {
                color: 'black',
            },
        ],
    },
    {
        featureType: 'transit.station.airport',
        elementType: 'geometry.fill',
        stylers: [
            {
                color: '#cfb2db',
            },
        ],
    },
    {
        featureType: 'water',
        elementType: 'geometry',
        stylers: [
            {
                color: '#a2daf2',
            },
        ],
    },
];

const createClusterIcon = (cluster) => {
    const count = cluster.getSize();

    // Define your SVG here. You can make it dynamic based on the count.
    const svg = `
    <svg width="62" height="62" viewBox="0 0 62 62" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M31 62C48.1208 62 62 48.1208 62 31C62 13.8792 48.1208 0 31 0C13.8792 0 0 13.8792 0 31C0 48.1208 13.8792 62 31 62ZM58.6439 30.5142C58.6439 46.0498 46.0498 58.6439 30.5142 58.6439C14.9787 58.6439 2.38462 46.0498 2.38462 30.5142C2.38462 14.9787 14.9787 2.38462 30.5142 2.38462C46.0498 2.38462 58.6439 14.9787 58.6439 30.5142Z" fill="#33567B"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M31 58C45.9117 58 58 45.9117 58 31C58 16.0883 45.9117 4 31 4C16.0883 4 4 16.0883 4 31C4 45.9117 16.0883 58 31 58ZM31 49.6C41.2725 49.6 49.6 41.2725 49.6 31C49.6 20.7275 41.2725 12.4 31 12.4C20.7275 12.4 12.4 20.7275 12.4 31C12.4 41.2725 20.7275 49.6 31 49.6Z" fill="#33567B"/>
<path d="M47 31C47 39.8366 39.8366 47 31 47C22.1634 47 15 39.8366 15 31C15 22.1634 22.1634 15 31 15C39.8366 15 47 22.1634 47 31Z" fill="#33567B"/>
</svg>
  `;

    // Convert SVG string to Base64
    const encoded = window.btoa(svg);

    return {
        url: 'data:image/svg+xml;base64,' + encoded,
        height: 62,
        width: 62,
    };
};
const clusterLayerZIndex = 99000;
const markerIconSvg =
    '<svg width="27" height="39" viewBox="0 0 27 39" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M13.562 39C0.722035 32.7955 -9.1072 0 13.562 0C36.2297 0 26.015 31.9091 13.562 39Z" fill="#xxxxxx"/></svg>';
const userIconSvg =
    '<svg width="16" height="27" viewBox="0 0 16 27" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.4 16.5819V24.5049L7.513 26.1749C7.753 26.5349 8.283 26.5349 8.523 26.1749L9.636 24.5049V16.5819C9.10253 16.6829 8.56093 16.7348 8.018 16.7369C7.47506 16.7348 6.93347 16.6829 6.4 16.5819ZM8.018 0.555908C7.06184 0.555908 6.11505 0.744237 5.23168 1.11014C4.34831 1.47605 3.54566 2.01236 2.86955 2.68846C2.19345 3.36457 1.65714 4.16722 1.29123 5.05059C0.925328 5.93396 0.737 6.88075 0.737 7.83691C0.737 8.79306 0.925328 9.73985 1.29123 10.6232C1.65714 11.5066 2.19345 12.3092 2.86955 12.9854C3.54566 13.6615 4.34831 14.1978 5.23168 14.5637C6.11505 14.9296 7.06184 15.1179 8.018 15.1179C9.94904 15.1179 11.801 14.3508 13.1664 12.9854C14.5319 11.6199 15.299 9.76795 15.299 7.83691C15.299 5.90587 14.5319 4.05392 13.1664 2.68846C11.801 1.32301 9.94904 0.555908 8.018 0.555908ZM8.018 4.39891C7.10651 4.39997 6.23266 4.76252 5.58814 5.40704C4.94362 6.05157 4.58106 6.92542 4.58 7.83691C4.58 7.99789 4.51605 8.15229 4.40221 8.26612C4.28838 8.37996 4.13399 8.44391 3.973 8.44391C3.81201 8.44391 3.65762 8.37996 3.54379 8.26612C3.42995 8.15229 3.366 7.99789 3.366 7.83691C3.36759 6.60361 3.85822 5.42128 4.73029 4.5492C5.60237 3.67713 6.7847 3.1865 8.018 3.18491C8.17899 3.18491 8.33338 3.24886 8.44721 3.36269C8.56105 3.47653 8.625 3.63092 8.625 3.79191C8.625 3.95289 8.56105 4.10729 8.44721 4.22112C8.33338 4.33496 8.17899 4.39891 8.018 4.39891Z" fill="#33567B"/></svg>';

// Pre-create a marker for all the colors, taking care of the nested colors
// colors from Colors.json = { 'dark': { 'blue': '#33567B', ... }, 'light': { 'blue': '#98CCE1', ... }, ... }
const markerIconAllColors = {};

for (let it = 0; it < Object.keys(Colors).length; it++) {
    const type = Object.keys(Colors)[it];
    for (let i = 0; i < Object.keys(Colors[type]).length; i++) {
        const color = Object.keys(Colors[type])[i];
        markerIconAllColors[`${type}-${color}`] = {
            url: 'data:image/svg+xml;base64,' + window.btoa(markerIconSvg.replace('#xxxxxx', Colors[type][color])),
            scaledSize: { width: 27, height: 39 },
            labelOrigin: { x: 13, y: 17 },
        };
    }
}

const markerIcon = {
    url: 'data:image/svg+xml;base64,' + markerIconAllColors['light-blue'],
    scaledSize: { width: 27, height: 39 },
    labelOrigin: { x: 13, y: 17 },
};

const userIconSvgEcoded = window.btoa(userIconSvg);

const userIcon = {
    url: 'data:image/svg+xml;base64,' + userIconSvgEcoded,
    scaledSize: { width: 16, height: 27 },
    zIndex: clusterLayerZIndex + 4000,
};
const renderer = ({ count, position }) =>
    new google.maps.Marker({
        label: { text: String(count), color: 'white' },
        position,
        icon: createClusterIcon({ getSize: () => count }),
        // adjust zIndex to make sure the label is visible
        zIndex: clusterLayerZIndex - 500 + count,
    });

const call = async (loc = null, fitBounds = false) => {
    globalIndex.value++;

    if (loc && loc?.lat && loc?.lng) {
        variables.value.mapQuery = {
            coordinate: loc,
            radius: 999999,
        };
    } else {
        const theLocation = toRaw(defaultLoc.value);
        variables.value.mapQuery = {
            coordinate: theLocation,
            radius: 999999,
        };
    }
    variables.value.orderBy = 'distance';

    const { data: res } = await client.executeQuery({
        variables: variables.value,
        query: `
            query FetchEntries(
                $lang: [String],
                $mapQuery: Ether_MapQuery,
                $orderBy: String
            ) {
                stores: entries (
                    section: "pointsDeVente",
                    site: $lang,
                    type: "pointDeVente",
                    groceryMap: $mapQuery,
                    orderBy: $orderBy
                ) {
                    title,
                    ... on pointDeVente_Entry {
                        map: groceryMap {
                            distance,
                            address,
                            lat,
                            lng
                        }
                        typeDetaillant {
                            ... on typeDetaillants_Entry {
                                title,
                                color: palette
                            }
                        }
                    }
                },
                total: entryCount (
                    section: "pointsDeVente",
                    site: $lang,
                    type: "pointDeVente",
                    groceryMap: $mapQuery,
                    orderBy: $orderBy
                )
            }
        `,
        cachePolicy: 'cache-and-network',
    });

    // const json = await res.json();
    stores.value.push(...res.stores);
    isSearchingNormal.value = false;
    isSearchingPostCode.value = false;
    firstRun.value = false;

    // link = json.meta.pagination.links.next;
    res.stores.forEach((item, index) => {
        markers.value.push({
            position: {
                lat: item.map.lat,
                lng: item.map.lng,
            },
            store: {
                item,
            },
            markerNum: (index + 1).toString(),
        });
    });

    const mymap = await gmap.value.$mapPromise;
    if (fitBounds) {
        const bounds = new google.maps.LatLngBounds();
        res.stores.forEach((item, index) => {
            bounds.extend(new google.maps.LatLng(item.map.lat, item.map.lng));
        });
        mymap.fitBounds(bounds);

        // Put the initial marker at the center of the bounds
        unclusteredMarkers.value.push({
            position: {
                lat: bounds.getCenter().lat(),
                lng: bounds.getCenter().lng(),
            },
        });
    } else {
        mymap.setZoom(defaultViewZoom.value);
    }
    const newCenter = await getCenterWithOffset(loc);
    mymap.setCenter(newCenter);
};

const getCenterWithOffset = async (coords = null) => {
    const offsety = 0.01;
    const offsetx = window.innerWidth >= 1024 ? window.innerWidth / -5 : 0;

    const mymap = await gmap.value.$mapPromise;

    let mapCenter = mymap.getCenter();
    let scale = Math.pow(2, mymap.getZoom());
    let latlng = new google.maps.LatLng(mapCenter.lat(), mapCenter.lng());
    if (coords !== null) {
        latlng = new google.maps.LatLng(coords.lat, coords.lng);
    }
    let worldCoordinateCenter = mymap.getProjection().fromLatLngToPoint(latlng);
    let pixelOffset = new google.maps.Point(offsetx / scale || 0, offsety / scale || 0);

    let worldCoordinateNewCenter = new google.maps.Point(worldCoordinateCenter.x - pixelOffset.x, worldCoordinateCenter.y + pixelOffset.y);
    let newCenter = mymap.getProjection().fromPointToLatLng(worldCoordinateNewCenter);

    return newCenter;
};

watch([offset], () => call(userLoc.value));

const loadMore = () => {
    isLoadingMore.value = true;
    // Fake loading to not spook the user by showing things too fast
    window.setTimeout(
        () => {
            displayedStoresCount.value += loadMoreIncrement;
            isLoadingMore.value = false;
        },
        Math.random() + 150 + 500,
    );
};

// let postcodeCache = null;

const ga = new GA({ category: 'midBody', action: 'clicExterne' });

const scrolledOnce = ref(false);

const searchWithPostCode = async () => {
    if (isSearching.value) return;

    isSearchingPostCode.value = true;
    markers.value = [];
    unclusteredMarkers.value = [];

    if (postcode.value != '') {
        let url = 'https://maps.googleapis.com/maps/api/geocode/json';
        url += `?address=${postcode.value}`;
        url += '&key=AIzaSyDLdfOZu7bqwSFFhBKSJM0r4sy3Pwz4b3U';
        const res = await fetch(url);
        const json = await res.json();
        if (json.results.length > 0) {
            userLoc.value = {
                lat: json.results[0].geometry.location.lat,
                lng: json.results[0].geometry.location.lng,
            };

            // Add point to the map for the user location
            unclusteredMarkers.value.push({
                position: {
                    lat: userLoc.value.lat,
                    lng: userLoc.value.lng,
                },
            });
        }
    }

    if (!userLoc.value) {
        return;
    }

    ga.send({ label: postcode.value, camelCase: false });

    // Reset stores
    stores.value.length = 0;

    await call(userLoc.value);

    if (!scrolledOnce.value) {
        lenis.value.scrollTo('#map-wrapper', {
            offset: -200,
        });
        scrolledOnce.value = true;
    }
};
const search = async () => {
    if (isSearching.value) return;

    postcode.value = '';
    isSearchingNormal.value = true;
    markers.value = [];
    unclusteredMarkers.value = [];

    // Try to get user's location
    try {
        const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 0,
            });
        });

        ga.send({ label: position, camelCase: false });

        userLoc.value = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
        };

        // Add point to the map for the user location
        unclusteredMarkers.value.push({
            position: {
                lat: userLoc.value.lat,
                lng: userLoc.value.lng,
            },
        });
    } catch (err) {
        console.warn('Could not get user location:', err);
        // Fall back to default location
        userLoc.value = defaultLoc.value;
    }

    // Reset stores
    stores.value.length = 0;

    await call(userLoc.value);
    if (!scrolledOnce.value) {
        lenis.value.scrollTo('#map-wrapper', {
            offset: -200,
        });
        scrolledOnce.value = true;
    }
};

// const { data } = await client.executeQuery({
//     variables,
//     query: `
//         query FetchEntries($lang: [String]) {
//             location: entry (section: "location", site: $lang) {
//                 ... on location_Entry {
//                     placeholder: locationSaleSpecialistPlaceHolder,
//                     ctaMap: locationCtaMap,
//                     error: locationSaleSpecialistErrorError,
//                     incomplete: locationSaleSpecialistErrorIncomplete,
//                     empty: locationSaleSpecialistErrorEmpty
//                 }
//             },
//         }
//     `,
// });

const openMarker = async (id) => {
    if (!gmap.value) return;

    // If clicking the same marker that's already open, just return
    if (openedMarkerID.value === id) {
        return;
    }

    // Wait for map to be ready
    const map = await gmap.value.$mapPromise;

    const marker = markers.value[id];
    if (!marker) return;

    // Define zoom levels

    // Check if marker would be clustered at current zoom
    const pixelBounds = map.getBounds();
    if (pixelBounds && markerClusterer.value) {
        const currentZoom = map.getZoom();

        // Only zoom in if we're too far out
        if (currentZoom < defaultViewZoom.value) {
            // Set to our standard viewing zoom level
            await new Promise((resolve) => {
                map.setZoom(defaultViewZoom.value);
                // Wait for zoom animation to complete
                google.maps.event.addListenerOnce(map, 'idle', resolve);
            });
        }

        // Small delay to ensure cluster has disbanded
        await new Promise((resolve) => setTimeout(resolve, 300));
    }

    // Get the marker's position as LatLng
    const markerPosition = await getCenterWithOffset(marker.position);
    // Just pan to the marker if we're already zoomed in enough
    map.panTo(markerPosition);

    // Now open the info window
    openedMarkerID.value = id;
};

onMounted(async () => {
    // Load all points by default at first
    await call(defaultLoc.value, true);
});

// Add debounced watch for search term
watch(searchTerm, (newValue) => {
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
        debouncedSearchTerm.value = newValue;
    }, 300); // 300ms debounce
});

const filteredStores = computed(() => {
    if (!debouncedSearchTerm.value) return stores.value;

    const searchLower = debouncedSearchTerm.value.toLowerCase();
    return stores.value.filter((store) => store.title.toLowerCase().includes(searchLower) || store.map.address.toLowerCase().includes(searchLower));
});

const filteredMarkers = computed(() => {
    if (!debouncedSearchTerm.value) return markers.value;

    const searchLower = debouncedSearchTerm.value.toLowerCase();
    return markers.value.filter(
        (marker) => marker.store.item.title.toLowerCase().includes(searchLower) || marker.store.item.map.address.toLowerCase().includes(searchLower),
    );
});
</script>

<template>
    <div class="LocationSaleSpecialist -mx-6 pb-12 lg:-mx-20">
        <div class="bg-pink w-full">
            <div class="justify-between bg-light-blue px-6 py-5 md:flex lg:px-22">
                <div class="mb-4 flex items-center text-white md:mb-0">
                    <button
                        class="inline-flex h-12 w-full items-center justify-center rounded-l-lg px-5 text-bright-blue md:w-auto"
                        :class="{
                            'bg-dark-blue hover:bg-dark-blue': !mapOpened,
                            'bg-darker-blue hover:bg-black': mapOpened,
                        }"
                        v-on:click="mapOpened = true"
                    >
                        <GeoMap class="h-6 w-auto" />
                        <transition
                            enter-active-class="transition-all duration-300"
                            leave-active-class="transition-all duration-300"
                            enter-from-class="w-0 opacity-0"
                            enter-to-class="w-14 opacity-100"
                            leave-from-class="w-14 opacity-100"
                            leave-to-class="w-0 opacity-0"
                        >
                            <span
                                v-show="mapOpened"
                                class="inline-block overflow-hidden whitespace-nowrap"
                            >
                                <span class="pl-3">Carte</span></span
                            >
                        </transition>
                    </button>
                    <button
                        class="inline-flex h-12 w-full items-center justify-center rounded-r-lg px-5 text-bright-blue md:w-auto"
                        :class="{
                            'bg-dark-blue hover:bg-dark-blue': mapOpened,
                            'bg-darker-blue hover:bg-black': !mapOpened,
                        }"
                        v-on:click="mapOpened = false"
                    >
                        <ListingIcon class="h-5 w-auto" />
                        <transition
                            enter-active-class="transition-all duration-300"
                            leave-active-class="transition-all duration-300"
                            enter-from-class="w-0 opacity-0"
                            enter-to-class="w-14 opacity-100"
                            leave-from-class="w-14 opacity-100"
                            leave-to-class="w-0 opacity-0"
                        >
                            <span
                                v-show="!mapOpened"
                                class="inline-block overflow-hidden whitespace-nowrap"
                            >
                                <span class="pl-3">Liste</span></span
                            >
                        </transition>
                    </button>
                </div>

                <!-- Search bar here, centered -->
                <div
                    v-if="!mapOpened"
                    class="SearchBar__input flex items-center rounded-lg bg-white text-dark-blue ring-2 ring-light-blue/30 transition focus-within:ring-light-blue"
                >
                    <MagnifyingGlass class="ml-2 mr-4 h-auto w-6 shrink-0 fill-current text-light-blue" />
                    <input
                        v-model="searchTerm"
                        tabindex="1"
                        type="text"
                        class="w-full rounded-lg py-3 placeholder:text-light-blue"
                        placeholder="Filtrer par nom"
                    />
                    <button
                        v-if="searchTerm !== ''"
                        class="h-auto w-8 shrink-0 text-light-blue"
                        v-on:click="searchTerm = ''"
                    >
                        <svg
                            viewBox="0 0 50 50"
                            class="w-full"
                            fill-rule="evenodd"
                        >
                            <path
                                class="fill-current"
                                d="M32.6,19.5L18.8,32c-0.4,0.4-1.1,0.4-1.4,0c-0.4-0.4-0.3-1.1,0.1-1.4L31.2,18c0.4-0.4,1.1-0.4,1.4,0 C33,18.5,33,19.1,32.6,19.5z"
                            />
                            <path
                                class="fill-current"
                                d="M30.6,32.5L17.9,18.9c-0.4-0.4-0.4-1.1,0-1.4s1-0.3,1.4,0.1l12.8,13.5c0.4,0.4,0.4,1.1,0,1.4C31.7,32.9,31,32.9,30.6,32.5z"
                            />
                        </svg>
                    </button>
                </div>
                <div
                    v-if="mapOpened"
                    class="LocationSaleSpecialist_geolocation-wrapper -mb-3 flex flex-col items-end gap-1"
                >
                    <div class="LocationSaleSpecialist__postcode-location flex w-full md:w-auto">
                        <input
                            v-model="postcode"
                            class="LocationSaleSpecialist__input w-full rounded-l-lg border-none bg-white px-5 py-3 font-sans text-lg font-normal transition duration-300 placeholder:normal-case placeholder:text-light-blue placeholder:transition placeholder:duration-300 focus:placeholder:opacity-50"
                            :class="{ 'opacity-50': isSearching }"
                            :placeholder="data.data.locationSaleSpecialistPlaceHolder"
                            type="text"
                            autocomplete="postal-code"
                            v-on:keyup.enter="searchWithPostCode"
                            :disabled="isSearching"
                        />
                        <button
                            class="LocationSaleSpecialist__submit flex shrink-0 cursor-pointer items-center rounded-r-lg bg-darker-blue p-3 text-lg font-bold text-white hover:bg-dark-blue lg:px-5"
                            :class="{
                                '!bg-dark-blue hover:!bg-dark-blue': isSearchingPostCode,
                                'opacity-50': isSearching,
                            }"
                            v-on:click="searchWithPostCode"
                            :disabled="isSearching"
                            :aria-disabled="isSearching"
                        >
                            <span
                                v-if="!isSearchingPostCode"
                                class="inline-block whitespace-nowrap text-center"
                            >
                                <MagnifyingGlass class="h-auto w-6 shrink-0 fill-current text-white" />
                            </span>
                            <span
                                v-if="isSearchingPostCode"
                                class="inline-flex h-auto w-6 items-center justify-center text-center"
                            >
                                <Loader
                                    bg="transparent"
                                    size="medium"
                                    line="white"
                                />
                            </span>
                        </button>
                    </div>
                    <button
                        class="mr-1 inline-flex items-center gap-2 text-xs"
                        :disabled="isSearching"
                        :aria-disabled="isSearching"
                        :class="{
                            'opacity-50': isSearching,
                        }"
                        v-on:click="search"
                    >
                        <span class="inline-flex items-center gap-3 text-center leading-none"> Utiliser ma position actuelle </span>
                        <span v-if="!isSearchingNormal"><GeoPin class="h-auto w-2 shrink-0" /></span>
                        <span
                            v-if="isSearchingNormal"
                            class="inline-flex w-2 items-center justify-center text-center"
                        >
                            <Loader
                                bg="transparent"
                                size="small"
                                line="white"
                            />
                        </span>
                    </button>
                </div>
            </div>
            <div
                id="map-wrapper"
                class="relative h-screen overflow-hidden bg-dark-blue text-white lg:h-[80vh]"
                :class="{
                    block: mapOpened,
                    hidden: !mapOpened,
                }"
            >
                <GMapMap
                    ref="gmap"
                    :center="center"
                    :zoom="7"
                    class="h-full w-full"
                    :options="{
                        styles: mapStyles,
                    }"
                >
                    <GMapMarker
                        v-for="(m, index) in unclusteredMarkers"
                        :key="globalIndex + index"
                        :position="m.position"
                        :icon="userIcon"
                        :zIndex="clusterLayerZIndex + 4000"
                    ></GMapMarker>
                    <GMapCluster
                        ref="markerClusterer"
                        :renderer="{ render: renderer }"
                    >
                        <GMapMarker
                            v-for="(m, index) in filteredMarkers"
                            :key="globalIndex + index"
                            :position="m.position"
                            :clickable="true"
                            :icon="markerIconAllColors[m?.store?.item?.typeDetaillant?.[0]?.color ?? 'dark-blue']"
                            :label="{
                                text: m.markerNum,
                                color: '#FFFFFF',
                                fontSize: m.markerNum >= 100 ? '12px' : '16px',
                                fontWeight: 'bold',
                            }"
                            :zIndex="clusterLayerZIndex - 500"
                            v-on:click="openMarker(index)"
                        >
                            <GMapInfoWindow
                                v-if="!m?.isUser"
                                :closeclick="true"
                                :opened="openedMarkerID === index"
                                v-on:closeclick="openedMarkerID = null"
                            >
                                <div class="text-sm font-bold text-dark-blue">
                                    {{ m.store.item.title }}
                                </div>
                                <div class="text-[#4B5563]">
                                    {{ m.store.item.map.address }}
                                </div>
                                <div class="flex w-full flex-col justify-between">
                                    <a
                                        class="LocationSaleSpecialist__link mt-4 flex items-center justify-between"
                                        :href="'https://www.google.ca/maps/place/' + m.store.item.map.address + '/'"
                                        target="_blank"
                                        v-on:click="ga.send({ label: m.store.item.map.address, camelCase: false })"
                                    >
                                        <div
                                            v-if="m.store.item.map.distance"
                                            class="LocationSaleSpecialist__distance font-light text-[#4B5563]"
                                        >
                                            {{ m.store.item.map.distance.toFixed(1).replace('.', ',') }} km
                                        </div>
                                        <Btn
                                            color="currentColor"
                                            size="small"
                                            text="Directions"
                                            :up="true"
                                        />
                                    </a>
                                </div>
                            </GMapInfoWindow>
                        </GMapMarker>
                    </GMapCluster>
                </GMapMap>
                <template v-if="stores.length > 0 && !isSearching">
                    <div
                        data-lenis-prevent
                        class="LocationSaleSpecialist__stores scrollbar-width-thin absolute right-22 top-[10vh] hidden h-[60vh] w-1/3 overflow-auto rounded-lg bg-white p-2 shadow-lg lg:flex lg:flex-col"
                    >
                        <button
                            v-for="(shopStore, shopStoreIndex) in filteredStores"
                            :key="'list-item-' + shopStoreIndex"
                            class="LocationSaleSpecialist__button group/list-item rounded hover:bg-black/5"
                            v-on:click.prevent="openMarker(shopStoreIndex)"
                        >
                            <div
                                class="flex h-full items-start justify-between border-t border-black/10 px-2 py-4 text-left text-dark-blue group-first/list-item:border-t-transparent group-hover/list-item:border-transparent group-hover-next/list-item:border-transparent"
                            >
                                <div class="mr-3 mt-1 flex w-12 flex-col items-center text-center">
                                    <svg
                                        class="mb-1 w-4"
                                        viewBox="0 0 27 40"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        :class="[shopStore.typeDetaillant?.[0]?.color ? `text-${shopStore.typeDetaillant?.[0]?.color}` : 'text-dark-blue']"
                                    >
                                        <path
                                            fill-rule="evenodd"
                                            clip-rule="evenodd"
                                            d="M13.5622 39.2031C0.722279 32.9986 -9.10695 0.20311 13.5622 0.20311C36.23 0.20311 26.0152 32.1122 13.5622 39.2031Z"
                                            fill="currentColor"
                                        />
                                        <text
                                            fill="white"
                                            x="13.5"
                                            y="17"
                                            style="white-space: pre"
                                            :font-size="shopStoreIndex >= 100 ? '12' : '16'"
                                            font-weight="bold"
                                            text-anchor="middle"
                                            alignment-baseline="central"
                                            letter-spacing="0em"
                                        >
                                            {{ shopStoreIndex + 1 }}
                                        </text>
                                    </svg>

                                    <div
                                        v-if="shopStore.map.distance"
                                        class="LocationSaleSpecialist__distance whitespace-nowrap text-center text-[14px] font-light"
                                    >
                                        {{ shopStore.map.distance.toFixed(1).replace('.', ',') }} km
                                    </div>
                                </div>
                                <div class="w-full">
                                    <div class="LocationSaleSpecialist__top mb-2 flex items-center">
                                        <h5 class="text-lg font-bold">
                                            {{ shopStore.title }}
                                        </h5>
                                        <div
                                            v-if="shopStore?.typeDetaillant?.[0]"
                                            class="LocationSaleSpecialist__tag ml-auto rounded-full px-2.5 py-1 text-sm font-bold text-white"
                                            :class="[shopStore.typeDetaillant?.[0]?.color ? `bg-${shopStore.typeDetaillant?.[0]?.color}` : 'bg-dark-blue']"
                                        >
                                            {{ shopStore.typeDetaillant?.[0]?.title }}
                                        </div>
                                    </div>
                                    <div class="flex text-[#4B5563]">
                                        <div
                                            class="LocationSaleSpecialist__address text-sm"
                                            v-html="shopStore?.map?.address ?? ''"
                                        />
                                    </div>
                                </div>
                            </div>
                        </button>
                    </div>
                </template>
            </div>
            <div
                class="list-wrapper mx-auto max-w-5xl px-6 py-12 xl:px-20"
                :class="{
                    hidden: mapOpened,
                    block: !mapOpened,
                }"
            >
                <div class="flex h-12 w-full items-start justify-center">
                    <Loader
                        v-if="isSearching"
                        bg="white"
                        line="dark-blue"
                    />
                </div>
                <template v-if="stores.length > 0 && !isSearching">
                    <div class="LocationSaleSpecialist__stores grid gap-6 xl:grid-cols-2 xl:gap-12">
                        <div
                            v-for="(shopStore, shopStoreIndex) in filteredStores.slice(0, displayedStoresCount)"
                            :key="'list-item-card-' + shopStoreIndex"
                            class="LocationSaleSpecialist__card flex h-full flex-col justify-between rounded-xl bg-white p-6 text-dark-blue xl:p-8"
                        >
                            <div class="LocationSaleSpecialist__top">
                                <h5 class="h5">
                                    {{ shopStore.title }}
                                </h5>
                                <p
                                    class="LocationSaleSpecialist__address"
                                    v-html="shopStore?.map?.address ?? ''"
                                />
                            </div>
                            <a
                                v-if="shopStore?.map?.address"
                                class="LocationSaleSpecialist__link mt-4 block border-t-2 border-dark-blue pt-6 xl:pt-8"
                                :href="'https://www.google.ca/maps/place/' + shopStore.map.address + '/'"
                                target="_blank"
                                v-on:click="ga.send({ label: shopStore.map.address, camelCase: false })"
                            >
                                <div class="wrapper-distance mb-4 flex items-center gap-4">
                                    <svg
                                        class="inline-block h-auto w-4"
                                        viewBox="0 0 27 40"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fill-rule="evenodd"
                                            clip-rule="evenodd"
                                            d="M13.5622 39.2031C0.722279 32.9986 -9.10695 0.20311 13.5622 0.20311C36.23 0.20311 26.0152 32.1122 13.5622 39.2031Z"
                                            fill="#F1716F"
                                        />
                                        <text
                                            fill="white"
                                            x="13.5"
                                            y="17"
                                            style="white-space: pre"
                                            :font-size="shopStoreIndex >= 100 ? '12' : '16'"
                                            font-weight="bold"
                                            text-anchor="middle"
                                            alignment-baseline="central"
                                            letter-spacing="0em"
                                        >
                                            {{ shopStoreIndex + 1 }}
                                        </text>
                                    </svg>
                                    <div
                                        v-if="shopStore.map.distance"
                                        class="LocationSaleSpecialist__distance text-3xl font-medium"
                                    >
                                        {{ shopStore.map.distance.toFixed(1).replace('.', ',') }} km
                                    </div>
                                </div>
                                <Btn
                                    color="currentColor"
                                    size="small"
                                    :text="data.data.locationCtaMap"
                                    :up="true"
                                />
                            </a>
                        </div>
                    </div>
                    <div
                        v-if="displayedStoresCount < filteredStores.length"
                        class="mt-8 flex justify-center"
                    >
                        <button
                            class="LocationSaleSpecialist__submit flex shrink-0 cursor-pointer items-center rounded-lg bg-darker-blue p-3 text-lg font-bold text-white hover:bg-dark-blue lg:px-5"
                            :class="{
                                '!bg-dark-blue hover:!bg-dark-blue': isLoadingMore,
                            }"
                            v-on:click="loadMore"
                        >
                            <span
                                v-if="!isSearching"
                                class="inline-block text-center"
                                >Charger plus</span
                            >
                            <span
                                v-if="isLoadingMore"
                                class="inline-flex w-6 items-center justify-end text-right"
                            >
                                <Loader
                                    bg="transparent"
                                    size="small"
                                    line="white"
                                />
                            </span>
                            <span
                                v-if="!isLoadingMore"
                                class="inline-flex w-6 items-center justify-end text-right"
                                >+</span
                            >
                        </button>
                    </div>
                </template>
                <template v-if="firstRun">
                    <p class="text-center text-lg opacity-50"> Veuillez entrer un code postal pour initier la recherche. </p>
                </template>
            </div>
        </div>
    </div>
</template>
